import type { ReactNode } from 'react'

export type Tone = 'green' | 'red' | 'amber' | 'blue' | 'purple' | 'gray'

export function Card({
  title,
  subtitle,
  action,
  children,
  className = '',
  tight = false,
}: {
  title?: ReactNode
  subtitle?: ReactNode
  action?: ReactNode
  children: ReactNode
  className?: string
  tight?: boolean
}) {
  return (
    <div className={`card ${tight ? 'tight' : ''} ${className}`}>
      {(title || action) && (
        <div className="card-head">
          <div>
            {title && <h2>{title}</h2>}
            {subtitle && <div className="help">{subtitle}</div>}
          </div>
          {action}
        </div>
      )}
      {children}
    </div>
  )
}

export function Stat({
  label,
  value,
  sub,
  tone,
}: {
  label: ReactNode
  value: ReactNode
  sub?: ReactNode
  tone?: 'pos' | 'neg' | 'muted'
}) {
  return (
    <div className="card">
      <div className="stat-label">{label}</div>
      <div className={`stat-value ${tone ?? ''}`}>{value}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  )
}

export function Pill({ tone = 'gray', children }: { tone?: Tone; children: ReactNode }) {
  return <span className={`pill ${tone}`}>{children}</span>
}

export function Button({
  children,
  onClick,
  variant = 'default',
  disabled,
  size,
  type = 'button',
  title,
}: {
  children: ReactNode
  onClick?: () => void
  variant?: 'default' | 'primary' | 'danger' | 'ghost'
  disabled?: boolean
  size?: 'sm'
  type?: 'button' | 'submit'
  title?: string
}) {
  const cls = ['btn', variant === 'default' ? '' : variant, size === 'sm' ? 'sm' : ''].join(' ').trim()
  return (
    <button className={cls} onClick={onClick} disabled={disabled} type={type} title={title}>
      {children}
    </button>
  )
}

export function Field({ label, hint, children }: { label: ReactNode; hint?: ReactNode; children: ReactNode }) {
  return (
    <div className="field">
      <label>
        <span>{label}</span>
        {hint && <span className="faint">{hint}</span>}
      </label>
      {children}
    </div>
  )
}

export function NumberField({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  hint,
  suffix,
}: {
  label: ReactNode
  value: number
  onChange: (n: number) => void
  min?: number
  max?: number
  step?: number
  hint?: ReactNode
  suffix?: string
}) {
  return (
    <Field label={label} hint={hint ?? suffix}>
      <input
        className="input mono"
        type="number"
        value={Number.isFinite(value) ? value : ''}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
      />
    </Field>
  )
}

export function TextField({
  label,
  value,
  onChange,
  placeholder,
  hint,
}: {
  label: ReactNode
  value: string
  onChange: (v: string) => void
  placeholder?: string
  hint?: ReactNode
}) {
  return (
    <Field label={label} hint={hint}>
      <input className="input" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </Field>
  )
}

export function TextArea({
  label,
  value,
  onChange,
  placeholder,
  rows = 3,
}: {
  label: ReactNode
  value: string
  onChange: (v: string) => void
  placeholder?: string
  rows?: number
}) {
  return (
    <Field label={label}>
      <textarea value={value} placeholder={placeholder} rows={rows} onChange={(e) => onChange(e.target.value)} />
    </Field>
  )
}

export function SelectField<T extends string>({
  label,
  value,
  options,
  onChange,
  hint,
}: {
  label: ReactNode
  value: T
  options: Array<{ value: T; label: string }>
  onChange: (v: T) => void
  hint?: ReactNode
}) {
  return (
    <Field label={label} hint={hint}>
      <select value={value} onChange={(e) => onChange(e.target.value as T)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </Field>
  )
}

export function RangeField({
  label,
  value,
  onChange,
  min = 0,
  max = 1,
  step = 0.01,
  display,
}: {
  label: ReactNode
  value: number
  onChange: (n: number) => void
  min?: number
  max?: number
  step?: number
  display?: ReactNode
}) {
  return (
    <Field label={label} hint={display ?? value.toFixed(2)}>
      <input className="range" type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </Field>
  )
}

export function Toggle({ label, checked, onChange }: { label: ReactNode; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="row" style={{ alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 10 }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ width: 16, height: 16, accentColor: 'var(--accent)' }} />
      <span>{label}</span>
    </label>
  )
}

export function ProgressBar({ pct, tone }: { pct: number; tone?: string }) {
  const clamped = Math.max(0, Math.min(100, pct))
  return (
    <div className="progress">
      <span style={{ width: `${clamped}%`, background: tone }} />
    </div>
  )
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>
}

export function KV({ k, v }: { k: ReactNode; v: ReactNode }) {
  return (
    <div className="kv">
      <span className="k">{k}</span>
      <span className="v">{v}</span>
    </div>
  )
}

export function Banner({ tone = 'info', icon, children }: { tone?: 'info' | 'warn' | 'danger'; icon?: ReactNode; children: ReactNode }) {
  return (
    <div className={`banner ${tone}`}>
      <span className="banner-ico">{icon ?? (tone === 'danger' ? '\u26A0' : tone === 'warn' ? '\u26A0' : '\u2139')}</span>
      <div>{children}</div>
    </div>
  )
}

export function Tabs<T extends string>({ tabs, active, onChange }: { tabs: Array<{ id: T; label: string }>; active: T; onChange: (id: T) => void }) {
  return (
    <div className="tabs">
      {tabs.map((t) => (
        <div key={t.id} className={`tab ${active === t.id ? 'active' : ''}`} onClick={() => onChange(t.id)}>
          {t.label}
        </div>
      ))}
    </div>
  )
}

export function PageHead({ title, children }: { title: ReactNode; children?: ReactNode }) {
  return (
    <div className="page-head">
      <h1>{title}</h1>
      {children && <p>{children}</p>}
    </div>
  )
}
